/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { useState, forwardRef } from 'react';
import { css } from '@emotion/react';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import BirdiIcon, { Icons, ICONS_CODEPOINTS } from '@birdi/icons';
import { BirdiTheme, useTheme } from '@birdi/theme';
import CalendarSVG from '@birdi/icons/calendar.svg';

type CustomInputProps = {
  value: string;
  onClick: (event: React.MouseEvent) => void;
  icon: Icons;
  placeholder;
  isTimelineView: boolean;
};
const CustomInput = forwardRef<HTMLButtonElement, CustomInputProps>(
  ({ value, onClick, icon, placeholder, isTimelineView }, ref) => {
    const { theme: currentTheme } = useTheme() as { theme: BirdiTheme };

    const formatTimelineDate = (dateString: string) => {
      if (!dateString) return '';

      try {
        const date = new Date(dateString);
        return format(date, 'MMMM d, yyyy');
      } catch (error) {
        return dateString; // Fallback to original format if parsing fails
      }
    };

    const formatDisplayValue = () => {
      if (!value) return placeholder;

      if (isTimelineView) {
        // For timeline view, parse the value and format it
        // The value comes as "yyyy/MM/dd - yyyy/MM/dd" from the DatePicker
        const dateParts = value.split(' - ');
        if (dateParts.length === 2) {
          const startDate = formatTimelineDate(dateParts[0]);
          const endDate = formatTimelineDate(dateParts[1]);
          return `${startDate} - ${endDate}`;
        }
      }

      // Default format for non-timeline view
      return value;
    };

    return (
      <button
        type="button"
        css={(theme: BirdiTheme) => css`
          padding: 1rem 0.5rem;
          color: ${theme.darkGrey};
          background: none;
          display: flex;
          align-items: center;
          min-width: ${isTimelineView ? '15em' : `calc(20em - 5px)`};
        `}
        ref={ref}
        onClick={onClick}
      >
        {icon && (
          <>
            <BirdiIcon icon={icon} color={currentTheme.darkGrey} />
            <span>&nbsp;</span>
          </>
        )}
        {isTimelineView && (
          <>
            <CalendarSVG
              css={css`
                font-size: 1.25rem;
              `}
            />
            <span>&nbsp;&nbsp;</span>
          </>
        )}
        <span
          css={css`
            display: inline-block;
            font-size: 1rem;
            text-align: left;
          `}
        >
          {formatDisplayValue()}
        </span>
      </button>
    );
  },
);

interface DatePickerRangeProps {
  type: 'range';
  onChange: (dates: [string, string] | null) => void;
}

interface DatePickerSingleProps {
  type: 'single';
  onChange: (dates: string) => void;
}

type DatePickerProps = (DatePickerRangeProps | DatePickerSingleProps) & {
  minDate?: Date | null;
  defaultStartDate?: Date | null;
  defaultEndDate?: Date | null;
  icon?: Icons;
  placeholder?: string;
  isTimelineView?: boolean;
  isClearable?: boolean;
};

const clearIcon = `'\\${parseInt(ICONS_CODEPOINTS[Icons.Close], 10).toString(16)}'`;

const BirdiDatePicker = ({
  type,
  onChange,
  icon,
  placeholder = '',
  defaultStartDate = null,
  defaultEndDate = null,
  minDate = null,
  isTimelineView,
  isClearable = true,
}: DatePickerProps) => {
  const [localDates, setLocalDates] = useState([
    defaultStartDate,
    defaultEndDate,
  ]);

  // Sync with prop changes
  React.useEffect(() => {
    setLocalDates([defaultStartDate, defaultEndDate]);
  }, [defaultStartDate, defaultEndDate]);

  const updateDate = (dates: [Date, Date]) => {
    setLocalDates(dates);
    if (type === 'range') {
      if (dates[0] && dates[1]) {
        onChange([
          format(dates[0], 'yyyy/MM/dd'),
          format(dates[1], 'yyyy/MM/dd'),
        ]);
      } else if (!dates[0] && !dates[1]) {
        onChange(null);
      }
    }
    if (type === 'single') onChange(format(dates[0], 'yyyy/MM/dd'));
  };

  if (type === 'range') {
    return (
      <div
        css={(theme: Theme) => css`
          .react-datepicker__input-container {
            display: flex;
            align-items: center;
            border: solid 1px ${theme.mono30};
            min-width: ${isTimelineView ? '15em' : `calc(20em - 5px)`};
            height: 3em;
            border-radius: ${isTimelineView ? '10px' : '3px'};
            &:hover {
              border: solid 1px ${theme.deepOcean};
            }
          }
          .react-datepicker__close-icon {
            font-family: icons !important;
            position: relative;
            margin: 0;
            padding: 0;
            margin-right: 0.5rem;
            &:after {
              content: ${clearIcon};
              background-color: transparent;
              color: ${theme.darkGrey};
            }
          }
        `}
      >
        <DatePicker
          id="dates"
          selectsRange
          minDate={minDate}
          startDate={localDates[0]}
          endDate={localDates[1]}
          onChange={(dates) => updateDate(dates)}
          shouldCloseOnSelect={localDates[0] && !localDates[1]}
          customInput={
            <CustomInput icon={icon} isTimelineView={isTimelineView} />
          }
          isClearable={isClearable}
          placeholderText={placeholder}
        />
      </div>
    );
  }

  if (type === 'single') {
    throw new Error('Not yet implemented');
  }

  throw new Error('Invalid type for DatePicker');
};

export { BirdiDatePicker };
